export const EMPTY_QUILL_VALUE = '<p><br></p>';

const HTML_TAG_REGEX = /<[a-z][\s\S]*>/i;
const QUILL_HTML_START = /^\s*<(p|ul|ol|h[1-3])[\s>]/i;
const NON_QUILL_HTML = /<div\b|<p\b[^>]*\/>/i;

const escapeHtml = (value: string) =>
  value.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

/**
 * Quill only reliably handles a small HTML subset. Template/generated content
 * often uses <div> and <p />; feeding that back through a controlled Quill
 * strips the text and leaves empty bullets. Normalize to Quill-friendly HTML.
 */
export const normalizeForQuill = (value: string): string => {
  if (!value) return '';

  if (!HTML_TAG_REGEX.test(value)) {
    return value
      .split('\n')
      .map((line) => `<p>${escapeHtml(line) || '<br>'}</p>`)
      .join('');
  }

  if (QUILL_HTML_START.test(value) && !NON_QUILL_HTML.test(value)) {
    return value;
  }

  if (typeof DOMParser === 'undefined') {
    return value
      .replace(/<p\s*\/>/gi, '')
      .replace(/<\/?div\b[^>]*>/gi, (match) =>
        match.startsWith('</') ? '</p>' : '<p>'
      );
  }

  const doc = new DOMParser().parseFromString(value, 'text/html');
  const parts: string[] = [];

  const pushParagraph = (innerHtml: string) => {
    const trimmed = innerHtml.trim();
    if (!trimmed) return;
    // Keep blank lines: Quill represents them as <p><br></p>.
    parts.push(
      trimmed === '<br>' || trimmed === '<br/>'
        ? EMPTY_QUILL_VALUE
        : `<p>${trimmed}</p>`
    );
  };

  const walk = (node: Node) => {
    if (node.nodeType === Node.TEXT_NODE) {
      const text = node.textContent?.trim();
      if (text) parts.push(`<p>${escapeHtml(text)}</p>`);
      return;
    }

    if (node.nodeType !== Node.ELEMENT_NODE) return;

    const el = node as HTMLElement;
    const tag = el.tagName.toLowerCase();

    if (tag === 'ul' || tag === 'ol') {
      parts.push(el.outerHTML);
      return;
    }

    if (tag === 'p') {
      pushParagraph(el.innerHTML);
      return;
    }

    if (tag === 'br') {
      parts.push(EMPTY_QUILL_VALUE);
      return;
    }

    if (tag === 'h1' || tag === 'h2' || tag === 'h3') {
      parts.push(el.outerHTML);
      return;
    }

    const hasBlockChild = Array.from(el.children).some((child) =>
      ['DIV', 'P', 'UL', 'OL', 'H1', 'H2', 'H3'].includes(child.tagName)
    );

    if (hasBlockChild) {
      el.childNodes.forEach(walk);
      return;
    }

    pushParagraph(el.innerHTML);
  };

  doc.body.childNodes.forEach(walk);
  return parts.join('') || value;
};

export const isEmptyQuillValue = (value: string) =>
  !value ||
  value === EMPTY_QUILL_VALUE ||
  value.replace(/<(.|\n)*?>/g, '').trim().length === 0;
